import { Input, NumberField, TagsInput } from "~/components/ui";
import { useTranslation } from "react-i18next";

interface DefaultValueEditorProps {
  value?: string | string[];
  onChange?: (value?: string | string[]) => void;
  fieldType?: string;
}

const normalizeAsArray = (value?: string | string[]): string[] => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value) return [value];
  return [];
};

const normalizeAsString = (value?: string | string[]): string => {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.join(", ");
  return "";
};

const normalizeAsNumber = (value?: string | string[]): number | undefined => {
  if (typeof value === "string" && value) {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
};

/**
 * Pure controlled editor for `extraction.default_value`, driven by the parent
 * react-hook-form Controller (same form pattern as update-concept-modal).
 * The input widget follows the field type: tags for list[string], number
 * input for number (stored as string for the backend), plain text otherwise.
 */
export function DefaultValueEditor({ value, onChange, fieldType = "string" }: DefaultValueEditorProps) {
  const { t } = useTranslation("admin");
  const isListType = fieldType === "list[string]";
  const isNumberType = fieldType === "number";

  const normalizedListValue = normalizeAsArray(value);
  const normalizedStringValue = normalizeAsString(value);
  const normalizedNumberValue = normalizeAsNumber(value);

  if (isNumberType) {
    return (
      <NumberField
        value={normalizedNumberValue}
        onChange={(v) => onChange?.(v !== null ? String(v) : undefined)}
        placeholder="0"
        className="w-full"
        keyboard
        controls
      />
    );
  }

  if (isListType) {
    return (
      <TagsInput
        value={normalizedListValue}
        onChange={(v) => onChange?.(v.length > 0 ? v : undefined)}
        placeholder={t("schemaAdmin.defaultValue.listPlaceholder")}
        tokenSeparators={[","]}
        className="w-full"
      />
    );
  }

  return (
    <Input
      value={normalizedStringValue}
      onChange={(e) => onChange?.(e.target.value || undefined)}
      placeholder={t("schemaAdmin.defaultValue.staticPlaceholder")}
    />
  );
}
